'use client';
import { useState, useMemo, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '@/lib/api';
import { useAuthStore } from '@/store/auth.store';
import { notify } from '@/lib/toast';
import { MapPin, Globe, Phone, Mail, Users, Building2, ChevronLeft, Star, Calendar, Copy, Check, X, ChevronDown, Clock, ExternalLink } from 'lucide-react';
import Link from 'next/link';
import { getDisplayName } from '@/lib/displayName';
import { groupSlotsByDate, formatSlotDate, getSlotTime } from '@/components/specialists/StarRating';

const COLOR_MAP: Record<string, string> = {
  teal: 'var(--color-brand)', indigo: '#6366f1', amber: 'var(--color-warning)', red: 'var(--color-danger)', slate: '#334155',
};
const HOVER_MAP: Record<string, string> = {
  teal: 'var(--color-brand-dark)', indigo: '#4f46e5', amber: '#b45309', red: '#b91c1c', slate: '#1e293b',
};

function ServiceBookingModal({ service, org, accent, textOnAccent, onClose }: { service: any; org: any; accent: string; textOnAccent: string; onClose: () => void }) {
  const router = useRouter();
  const { isAuthenticated } = useAuthStore();
  const initialStep = service.specialists.length === 1 ? 2 : 1;
  const [step, setStep] = useState<1 | 2 | 3>(initialStep as 1 | 2 | 3);
  const [selectedSpecialist, setSelectedSpecialist] = useState<any>(service.specialists.length === 1 ? service.specialists[0] : null);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [selectedTime, setSelectedTime] = useState<string | null>(null);
  const [clientComment, setClientComment] = useState('');

  const dateFrom = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const dateTo = useMemo(() => { const d = new Date(); d.setDate(d.getDate() + 30); return d.toISOString().slice(0, 10); }, []);

  const { data: availData, isLoading: availLoading } = useQuery({
    queryKey: ['org-svc-avail', org.id, service.id, selectedSpecialist?.specialistId, dateFrom, dateTo],
    queryFn: () => api.get(`/organizations/${org.id}/services/${service.id}/availability`, {
      params: { date_from: dateFrom, date_to: dateTo }
    }).then(r => {
      const specialists = r.data.data?.specialists ?? [];
      return specialists.find((s: any) => s.id === selectedSpecialist?.specialistId) ?? null;
    }),
    enabled: !!selectedSpecialist && step === 2,
  });

  const availDates = useMemo(() => {
    if (!availData?.availability) return new Set<string>();
    return new Set(availData.availability.map((a: any) => a.date));
  }, [availData]);

  const slotsForDate = useMemo(() => {
    if (!selectedDate || !availData?.availability) return [];
    return availData.availability.find((a: any) => a.date === selectedDate)?.slots ?? [];
  }, [availData, selectedDate]);

  const calendarDates = useMemo(() => {
    const dates = [];
    const start = new Date(dateFrom + 'T00:00:00');
    for (let i = 0; i < 30; i++) {
      const d = new Date(start); d.setDate(start.getDate() + i);
      dates.push(d.toISOString().slice(0, 10));
    }
    return dates;
  }, [dateFrom]);

  const bookingMutation = useMutation({
    mutationFn: (data: any) => api.post('/bookings', data),
    onSuccess: (res) => { const bookingId = res.data.data?.id; onClose(); if (bookingId) router.push(`/bookings/${bookingId}`); },
    onError: (err: any) => notify.apiError(err, 'Ошибка бронирования'),
  });

  const offlineLocations = (org.locations ?? []).filter((l: any) => !l.isOnline && !l.isMobile);

  const handleBook = () => {
    if (!isAuthenticated) { notify.error('Войдите чтобы записаться'); return; }
    if (!selectedSpecialist || !selectedDate || !selectedTime) return;
    bookingMutation.mutate({
      specialist_id: selectedSpecialist.specialistId,
      booking_date: selectedDate,
      start_time: selectedTime,
      org_service_id: service.id,
      client_comment: clientComment || undefined,
    });
  };

  const formatDateLabel = (dateStr: string) => {
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'short', weekday: 'short' });
  };
  const formatDateShort = (dateStr: string) => {
    const d = new Date(dateStr + 'T00:00:00');
    return { day: d.getDate(), weekday: d.toLocaleDateString('ru-RU', { weekday: 'short' }) };
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-bg-surface rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="p-4 border-b border-border-subtle flex items-center justify-between">
          <div>
            <h3 className="font-bold text-text-primary">{service.title}</h3>
            <p className="text-xs text-gray-500 mt-0.5">{service.price ? `${Number(service.price).toLocaleString('ru-RU')} ₽ · ` : ''}{service.durationMinutes} мин</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-text-secondary"><X size={20} /></button>
        </div>
        <div className="p-4">
          {step === 1 && (
            <div>
              <p className="text-sm font-semibold text-text-ui mb-3">Выберите мастера</p>
              <div className="space-y-2">
                {service.specialists.map((link: any) => {
                  const u = link.specialist.user;
                  const name = getDisplayName(u);
                  return (
                    <button key={link.id} onClick={() => { setSelectedSpecialist(link); setSelectedDate(null); setSelectedTime(null); setStep(2); }}
                      className="w-full flex items-center gap-3 p-3 rounded-xl border transition-all text-left" style={{ borderColor: 'var(--color-border)' }}
                      onMouseEnter={e => (e.currentTarget.style.borderColor = accent)} onMouseLeave={e => (e.currentTarget.style.borderColor = 'var(--color-border)')}>
                      <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0 flex items-center justify-center text-white font-bold" style={{ background: `linear-gradient(135deg, ${accent}, ${accent}aa)` }}>
                        {u?.avatarUrl ? <img src={u.avatarUrl} alt={name} className="w-10 h-10 object-cover" /> : name.charAt(0)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-sm text-text-primary">{name}</p>
                        {link.specialist.rating > 0 && <div className="flex items-center gap-1 mt-0.5"><Star size={11} className="text-yellow-400 fill-yellow-400" /><span className="text-xs text-gray-500">{Number(link.specialist.rating).toFixed(1)}</span></div>}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          {step === 2 && (
            <div>
              {service.specialists.length > 1 && (
                <button onClick={() => { setStep(1); setSelectedDate(null); setSelectedTime(null); }} className="flex items-center gap-1 text-xs mb-3" style={{ color: accent }}>
                  <ChevronLeft size={14} /> Другой мастер
                </button>
              )}
              <p className="text-sm font-semibold text-text-ui mb-3">Выберите дату и время{selectedSpecialist && <span className="font-normal text-gray-400 ml-1">· {getDisplayName(selectedSpecialist.specialist.user)}</span>}</p>
              {availLoading ? (
                <div className="space-y-2">{[1,2,3].map(i => <div key={i} className="animate-pulse h-10 bg-bg-muted rounded-xl" />)}</div>
              ) : availDates.size === 0 ? (
                <p className="text-sm text-gray-400 text-center py-6">Нет доступного времени в ближайшие 30 дней</p>
              ) : (
                <>
                  <div className="overflow-x-auto pb-2 mb-4">
                    <div className="flex gap-1.5" style={{ minWidth: 'max-content' }}>
                      {calendarDates.map(dateStr => {
                        const hasSlots = availDates.has(dateStr);
                        const isSelected = selectedDate === dateStr;
                        const { day, weekday } = formatDateShort(dateStr);
                        return (
                          <button key={dateStr} onClick={() => { if (hasSlots) { setSelectedDate(dateStr); setSelectedTime(null); } }} disabled={!hasSlots}
                            className="flex flex-col items-center px-2 py-1.5 rounded-xl text-xs font-medium transition-all min-w-[44px]"
                            style={isSelected ? { backgroundColor: accent, color: textOnAccent } : hasSlots ? { backgroundColor: `${accent}15`, color: accent, cursor: 'pointer' } : { backgroundColor: 'var(--color-surface)', color: 'var(--color-border-light)', cursor: 'not-allowed' }}>
                            <span className="text-[10px] opacity-70">{weekday}</span>
                            <span className="text-sm font-bold">{day}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  {selectedDate && (
                    <div>
                      <p className="text-xs font-semibold text-gray-500 mb-2">{formatDateLabel(selectedDate)}</p>
                      <div className="flex flex-wrap gap-1.5">
                        {slotsForDate.map((time: string) => (
                          <button key={time} onClick={() => { setSelectedTime(time); setStep(3); }}
                            className="px-3 py-1.5 rounded-lg text-sm font-medium border transition-all"
                            style={selectedTime === time ? { backgroundColor: accent, color: textOnAccent, borderColor: accent } : { borderColor: 'var(--color-border)', color: 'var(--color-text-secondary)' }}>
                            {time}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  {!selectedDate && availDates.size > 0 && <p className="text-xs text-gray-400 text-center mt-2">Выберите дату выше</p>}
                </>
              )}
            </div>
          )}
          {step === 3 && (
            <div>
              <button onClick={() => setStep(2)} className="flex items-center gap-1 text-xs mb-3" style={{ color: accent }}><ChevronLeft size={14} /> Изменить время</button>
              <p className="text-sm font-semibold text-text-ui mb-3">Подтверждение записи</p>
              <div className="bg-bg-subtle rounded-xl p-3 space-y-2 mb-3">
                <div className="flex justify-between text-sm"><span className="text-gray-500">Услуга</span><span className="font-medium text-text-primary">{service.title}</span></div>
                <div className="flex justify-between text-sm"><span className="text-gray-500">Мастер</span><span className="font-medium text-text-primary">{getDisplayName(selectedSpecialist?.specialist?.user)}</span></div>
                <div className="flex justify-between text-sm"><span className="text-gray-500">Дата и время</span><span className="font-medium text-text-primary">{selectedDate && formatDateLabel(selectedDate)}, {selectedTime}</span></div>
                {service.price && <div className="flex justify-between text-sm"><span className="text-gray-500">Стоимость</span><span className="font-medium text-text-primary">{Number(service.price).toLocaleString('ru-RU')} ₽</span></div>}
                {offlineLocations.length > 0 && <div className="flex justify-between text-sm"><span className="text-gray-500">Адрес</span><span className="font-medium text-text-primary text-right max-w-[180px]">{offlineLocations[0].name}{offlineLocations[0].address ? `, ${offlineLocations[0].address}` : ''}</span></div>}
              </div>
              <textarea className="w-full border border-border rounded-xl px-3 py-2 text-sm mb-3" placeholder="Комментарий (необязательно)" rows={2} value={clientComment} onChange={e => setClientComment(e.target.value)} />
              <button onClick={handleBook} disabled={bookingMutation.isPending} className="w-full py-3 rounded-xl text-white font-semibold text-sm disabled:opacity-50" style={{ backgroundColor: accent }}>
                {bookingMutation.isPending ? 'Отправка...' : 'Записаться'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function BookingModal({ member, accent, textOnAccent, orgId, onClose }: { member: any; accent: string; textOnAccent: string; orgId: string; onClose: () => void }) {
  const router = useRouter();
  const queryClient = useQueryClient();
  const { isAuthenticated } = useAuthStore();
  const spec = member.specialist;
  const u = member.user;
  const name = getDisplayName(u);
  const specId = spec?.id;
  const [selectedService, setSelectedService] = useState<string | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<string | null>(null);
  const [expandedDate, setExpandedDate] = useState<string | null>(null);
  const [clientComment, setClientComment] = useState('');

  const { data: slots = [], isLoading: slotsLoading } = useQuery({
    queryKey: ['org-slots', specId],
    queryFn: () => api.get(`/specialists/${specId}/availability`).then(r => r.data.data),
    enabled: !!specId,
  });

  const futureSlots = useMemo(() => {
    const now = new Date();
    return (slots as any[]).filter((slot: any) => {
      if (slot.isBooked) return false;
      if (slot.organizationId && slot.organizationId !== orgId) return false;
      if (!slot.organizationId) return false;
      if (slot.startAt) return new Date(slot.startAt) > now;
      return true;
    });
  }, [slots, orgId]);

  const groupedSlots = useMemo(() => groupSlotsByDate(futureSlots), [futureSlots]);
  const selectedServiceData = useMemo(() => spec?.services?.find((s: any) => s.id === selectedService), [spec, selectedService]);
  const selectedSlotData = useMemo(() => futureSlots.find((s: any) => s.id === selectedSlot), [futureSlots, selectedSlot]);
  const slotEndTime = useMemo(() => {
    if (!selectedSlotData || !selectedServiceData?.durationMinutes) return null;
    const timeStr = getSlotTime(selectedSlotData);
    const [h, m] = timeStr.split(':').map(Number);
    const start = new Date(); start.setHours(h, m, 0, 0);
    const end = new Date(start.getTime() + selectedServiceData.durationMinutes * 60000);
    return `${String(end.getHours()).padStart(2,'0')}:${String(end.getMinutes()).padStart(2,'0')}`;
  }, [selectedSlotData, selectedServiceData]);

  const bookingMutation = useMutation({
    mutationFn: (data: any) => api.post('/bookings', data),
    onSuccess: () => { notify.success('Бронирование создано!'); queryClient.invalidateQueries({ queryKey: ['org-slots', specId] }); onClose(); router.push('/dashboard'); },
    onError: (err: any) => notify.apiError(err, 'Ошибка бронирования'),
  });

  const handleBook = () => {
    if (!isAuthenticated()) { router.push('/login'); return; }
    if (!selectedSlot || !selectedService) { notify.error('Выберите услугу и время'); return; }
    bookingMutation.mutate({ specialist_id: specId, service_id: selectedService, slot_id: selectedSlot, ...(clientComment.trim() && { client_comment: clientComment.trim() }) });
  };

  const services: any[] = spec?.services ?? [];

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-bg-surface w-full sm:max-w-lg sm:rounded-2xl rounded-t-2xl shadow-2xl max-h-[90vh] flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-border-subtle flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0 overflow-hidden" style={{ background: `linear-gradient(135deg, ${accent}, ${accent}cc)` }}>
              {u?.avatarUrl ? <img src={u.avatarUrl} alt={name} className="w-10 h-10 object-cover" /> : name.charAt(0).toUpperCase()}
            </div>
            <div>
              <p className="font-semibold text-text-primary text-sm">{name}</p>
              {spec?.rating > 0 && <div className="flex items-center gap-1"><Star size={11} className="text-yellow-400 fill-yellow-400" /><span className="text-xs text-gray-500">{Number(spec.rating).toFixed(1)}</span></div>}
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-bg-muted rounded-full transition-colors"><X size={18} className="text-gray-500" /></button>
        </div>
        <div className="overflow-y-auto flex-1 px-5 py-4 space-y-5">
          <div>
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2 flex items-center gap-1.5">
              <span className="w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold" style={{ background: selectedService ? accent : 'var(--color-border)', color: selectedService ? textOnAccent : 'var(--color-text-tertiary)' }}>{selectedService ? '✓' : '1'}</span>
              Выберите услугу
            </p>
            {services.length === 0 ? <p className="text-sm text-gray-400">Услуги не добавлены</p> : (
              <div className="space-y-2">
                {services.map((s: any) => (
                  <button key={s.id} onClick={() => { setSelectedService(s.id); setSelectedSlot(null); }}
                    className="w-full flex items-center justify-between px-4 py-3 rounded-xl border text-sm transition-all"
                    style={{ borderColor: selectedService === s.id ? accent : 'var(--color-border)', background: selectedService === s.id ? `${accent}0d` : 'white', color: selectedService === s.id ? accent : 'var(--color-text-secondary)' }}>
                    <span className="font-medium text-left">{s.title}</span>
                    <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                      {s.durationMinutes && <span className="text-xs text-gray-400 flex items-center gap-1"><Clock size={11} />{s.durationMinutes} мин</span>}
                      <span className="font-semibold">{s.price != null && !isNaN(Number(s.price)) ? Number(s.price).toLocaleString() + ' ₽' : 'договорная'}</span>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
          <div>
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2 flex items-center gap-1.5">
              <span className="w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold" style={{ background: selectedSlot ? accent : 'var(--color-border)', color: selectedSlot ? textOnAccent : 'var(--color-text-tertiary)' }}>{selectedSlot ? '✓' : '2'}</span>
              Выберите время
            </p>
            {slotsLoading ? <div className="space-y-2">{[1,2].map(i => <div key={i} className="animate-pulse h-10 bg-bg-muted rounded-xl" />)}</div>
              : futureSlots.length === 0 ? <div className="text-center py-5 bg-bg-subtle rounded-xl text-gray-400 text-sm">Нет свободных слотов</div>
              : (
                <div className="space-y-2 max-h-52 overflow-y-auto pr-1">
                  {groupedSlots.map(([dateKey, daySlots]) => (
                    <div key={dateKey} className="border border-border rounded-xl overflow-hidden">
                      <button onClick={() => setExpandedDate(expandedDate === dateKey ? null : dateKey)}
                        className="w-full flex items-center justify-between px-4 py-2.5 text-sm font-medium transition-colors"
                        style={{ background: expandedDate === dateKey ? `${accent}0d` : 'var(--color-surface-subtle)', color: expandedDate === dateKey ? accent : 'var(--color-text-secondary)' }}>
                        <span>{formatSlotDate(dateKey)}</span>
                        <div className="flex items-center gap-2"><span className="text-xs text-gray-400">{(daySlots as any[]).length} слотов</span><ChevronDown size={14} className={`transition-transform ${expandedDate === dateKey ? 'rotate-180' : ''}`} /></div>
                      </button>
                      {expandedDate === dateKey && (
                        <div className="grid grid-cols-3 gap-1.5 p-2 bg-bg-surface">
                          {(daySlots as any[]).map((slot: any) => (
                            <button key={slot.id} onClick={() => { setSelectedSlot(slot.id); setExpandedDate(dateKey); }}
                              className="py-2 rounded-lg text-xs font-medium transition-all border"
                              style={{ background: selectedSlot === slot.id ? accent : 'var(--color-surface-subtle)', color: selectedSlot === slot.id ? textOnAccent : 'var(--color-text-secondary)', borderColor: selectedSlot === slot.id ? accent : 'var(--color-border)' }}>
                              {getSlotTime(slot)}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
          </div>
          {selectedService && (
            <textarea value={clientComment} onChange={e => setClientComment(e.target.value)}
              placeholder="Комментарий для специалиста (необязательно)" maxLength={500} rows={2}
              className="w-full border border-border rounded-xl px-3 py-2.5 text-sm text-text-ui placeholder-gray-400 resize-none transition-all"
              onFocus={e => e.target.style.borderColor = accent} onBlur={e => e.target.style.borderColor = 'var(--color-border)'} />
          )}
        </div>
        <div className="flex-shrink-0 px-5 py-4 border-t border-border-subtle bg-bg-surface space-y-3">
          {(selectedServiceData || selectedSlotData) && (
            <div className="rounded-xl p-3 text-sm space-y-1.5" style={{ background: `${accent}0d`, border: `1px solid ${accent}33` }}>
              {selectedServiceData && <div className="flex justify-between items-center"><span className="text-gray-500">Услуга</span><span className="font-semibold text-text-primary text-right ml-2">{selectedServiceData.title}</span></div>}
              {selectedSlotData && <div className="flex justify-between items-center"><span className="text-gray-500">Время</span><span className="font-semibold text-text-primary">{formatSlotDate(selectedSlotData.date ?? selectedSlotData.startAt?.slice(0,10))}, {getSlotTime(selectedSlotData)}{slotEndTime && ` — ${slotEndTime}`}</span></div>}
              {selectedServiceData && <div className="flex justify-between items-center pt-1.5 mt-1" style={{ borderTop: `1px solid ${accent}33` }}><span className="text-gray-500">Итого</span><span className="font-bold text-base" style={{ color: accent }}>{selectedServiceData.price != null && !isNaN(Number(selectedServiceData.price)) ? Number(selectedServiceData.price).toLocaleString() + ' ₽' : 'договорная'}</span></div>}
            </div>
          )}
          <button onClick={handleBook} disabled={!selectedSlot || !selectedService || bookingMutation.isPending}
            className="w-full py-3 rounded-xl font-semibold flex items-center justify-center gap-2 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
            style={{ background: accent, color: textOnAccent }}>
            <Check size={18} />{bookingMutation.isPending ? 'Бронируем...' : 'Забронировать'}
          </button>
          <p className="text-center text-xs text-gray-400">Или <Link href={`/specialists/${specId}`} style={{ color: accent }} className="hover:underline">перейти на страницу специалиста</Link></p>
        </div>
      </div>
    </div>
  );
}

export default function OrgPageClient({ org, galleryPhotos }: { org: any; galleryPhotos: any[] }) {
  const router = useRouter();
  const { user } = useAuthStore();
  const [copied, setCopied] = useState(false);
  const [bookingMember, setBookingMember] = useState<any | null>(null);
  const [bookingService, setBookingService] = useState<any | null>(null);
  const [activeTab, setActiveTab] = useState<'services' | 'specialists'>('specialists');
  const [tabInitialized, setTabInitialized] = useState(false);

  const locations = org.locations ?? [];
  const members = org.members ?? [];
  const orgServices = org.orgServices ?? [];
  const hasServices = orgServices.length > 0;
  const isOwner = user?.id === org.ownerId;
  const accent = org.accentColor ?? COLOR_MAP[org.colorScheme ?? 'teal'] ?? COLOR_MAP.teal;
  const textOnAccent = org.textOnAccent ?? '#ffffff';
  const accentHover = HOVER_MAP[org.colorScheme ?? 'teal'] ?? HOVER_MAP.teal;
  const blocks = org.blocksVisibility ?? { gallery: true, hours: true, reviews: true, map: false };
  const workingHours: Record<string, string> = org.workingHours ?? {};
  const acceptedMembers = members.filter((m: any) => m.status === 'accepted');

  if (!tabInitialized) { setTabInitialized(true); if (hasServices) setActiveTab('services'); }

  const copySlug = () => {
    if (!org.slug) return;
    navigator.clipboard.writeText(`https://svoyprofi.ru/o/${org.slug}`);
    setCopied(true); setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="min-h-screen bg-bg-subtle">
      {bookingMember && <BookingModal member={bookingMember} accent={accent} textOnAccent={textOnAccent} orgId={org.id} onClose={() => setBookingMember(null)} />}
      {bookingService && <ServiceBookingModal service={bookingService} org={org} accent={accent} textOnAccent={textOnAccent} onClose={() => setBookingService(null)} />}

      {/* HERO */}
      <div style={{ backgroundColor: accent, color: textOnAccent }}>
        {org.coverUrl && <div className="h-40 overflow-hidden"><img src={org.coverUrl} alt="" className="w-full h-full object-cover opacity-40" /></div>}
        <div className="max-w-3xl mx-auto px-4 py-6">
          <button onClick={() => router.back()} className="flex items-center gap-1 text-sm mb-4 transition-colors" style={{ color: textOnAccent, opacity: 0.75 }} onMouseEnter={e => (e.currentTarget.style.opacity = '1')} onMouseLeave={e => (e.currentTarget.style.opacity = '0.75')}>
            <ChevronLeft size={16} /> Назад
          </button>
          <div className="flex items-start gap-4">
            <div className="w-16 h-16 rounded-2xl bg-white/20 flex items-center justify-center flex-shrink-0 text-2xl font-bold overflow-hidden border-2 border-white/30" style={{ color: textOnAccent }}>
              {org.logoUrl ? <img src={org.logoUrl} alt={org.name} className="w-16 h-16 object-cover" /> : org.name?.charAt(0).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <h1 className="text-xl font-bold" style={{ color: textOnAccent }}>{org.name}</h1>
              {org.description && <p className="mt-1 text-sm leading-relaxed" style={{ color: textOnAccent, opacity: 0.8 }}>{org.description}</p>}
              {org.slug && (
                <button onClick={copySlug} className="flex items-center gap-1.5 mt-2 text-xs transition-colors" style={{ color: textOnAccent, opacity: 0.6 }} onMouseEnter={e => (e.currentTarget.style.opacity = '1')} onMouseLeave={e => (e.currentTarget.style.opacity = '0.6')}>
                  {copied ? <Check size={12} /> : <Copy size={12} />} svoyprofi.ru/o/{org.slug}
                </button>
              )}
              <div className="flex flex-wrap gap-3 mt-2">
                {org.phone && <a href={`tel:${org.phone}`} className="flex items-center gap-1 text-xs transition-colors" style={{ color: textOnAccent, opacity: 0.8 }} onMouseEnter={e => (e.currentTarget.style.opacity = '1')} onMouseLeave={e => (e.currentTarget.style.opacity = '0.8')}><Phone size={12} />{org.phone}</a>}
                {org.email && <a href={`mailto:${org.email}`} className="flex items-center gap-1 text-xs transition-colors" style={{ color: textOnAccent, opacity: 0.8 }} onMouseEnter={e => (e.currentTarget.style.opacity = '1')} onMouseLeave={e => (e.currentTarget.style.opacity = '0.8')}><Mail size={12} />{org.email}</a>}
                {org.website && <a href={org.website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-xs transition-colors" style={{ color: textOnAccent, opacity: 0.8 }} onMouseEnter={e => (e.currentTarget.style.opacity = '1')} onMouseLeave={e => (e.currentTarget.style.opacity = '0.8')}><Globe size={12} />{org.website.replace(/^https?:\/\//, '')}</a>}
              </div>
            </div>
            {isOwner && <Link href="/dashboard?section=organization" className="flex-shrink-0 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors" style={{ backgroundColor: 'rgba(255,255,255,0.2)', color: textOnAccent }}>Управление</Link>}
          </div>
        </div>
      </div>

      {/* STATS */}
      <div className="max-w-3xl mx-auto px-4 -mt-1">
        <div className="grid grid-cols-3 gap-3 py-4">
          {acceptedMembers.length > 0 && <div className="bg-bg-surface rounded-xl border border-border-subtle shadow-sm p-3 text-center"><div className="text-xl font-bold text-text-primary">{acceptedMembers.length}</div><div className="text-xs text-gray-400 mt-0.5">{acceptedMembers.length === 1 ? 'специалист' : 'специалиста'}</div></div>}
          {locations.length > 0 && <div className="bg-bg-surface rounded-xl border border-border-subtle shadow-sm p-3 text-center"><div className="text-xl font-bold text-text-primary">{locations.length}</div><div className="text-xs text-gray-400 mt-0.5">{locations.length === 1 ? 'локация' : 'локации'}</div></div>}
          {hasServices && <div className="bg-bg-surface rounded-xl border border-border-subtle shadow-sm p-3 text-center"><div className="text-xl font-bold text-text-primary">{orgServices.length}</div><div className="text-xs text-gray-400 mt-0.5">{orgServices.length === 1 ? 'услуга' : orgServices.length < 5 ? 'услуги' : 'услуг'}</div></div>}
          <div className="bg-bg-surface rounded-xl border border-border-subtle shadow-sm p-3 text-center"><div className="text-xl font-bold" style={{ color: accent }}>Online</div><div className="text-xs text-gray-400 mt-0.5">запись</div></div>
          {org.avgRating && <div className="bg-bg-surface rounded-xl border border-border-subtle shadow-sm p-3 text-center"><div className="text-xl font-bold text-text-primary flex items-center justify-center gap-1"><Star size={16} className="text-yellow-400 fill-yellow-400" />{Number(org.avgRating).toFixed(1)}</div><div className="text-xs text-gray-400 mt-0.5">{org.totalReviews} {org.totalReviews === 1 ? 'отзыв' : org.totalReviews < 5 ? 'отзыва' : 'отзывов'}</div></div>}
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 pb-8 space-y-4">
        {(org.about || (blocks.hours && Object.keys(workingHours).length > 0)) && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {org.about && <div className="bg-bg-surface rounded-2xl border border-border-subtle shadow-sm p-5"><h2 className="text-sm font-bold text-text-primary mb-2">О нас</h2><p className="text-sm text-text-secondary leading-relaxed">{org.about}</p></div>}
            {blocks.hours && Object.keys(workingHours).length > 0 && (
              <div className="bg-bg-surface rounded-2xl border border-border-subtle shadow-sm p-5">
                <h2 className="text-sm font-bold text-text-primary mb-3 flex items-center gap-1.5"><Clock size={14} style={{ color: accent }} /> Часы работы</h2>
                <div className="space-y-1.5">{Object.entries(workingHours).map(([day, hours]) => <div key={day} className="flex justify-between text-sm"><span className="text-gray-500">{day}</span><span className={hours === 'выходной' ? 'text-gray-400' : 'font-medium text-text-primary'}>{hours}</span></div>)}</div>
              </div>
            )}
          </div>
        )}

        {locations.length > 0 && (
          <div className="bg-bg-surface rounded-2xl border border-border-subtle shadow-sm p-5">
            <h2 className="text-sm font-bold text-text-primary mb-3 flex items-center gap-1.5"><MapPin size={14} style={{ color: accent }} /> Адреса</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {locations.map((loc: any) => (
                <div key={loc.id} className="flex items-start gap-3 p-3 bg-bg-subtle rounded-xl">
                  <MapPin size={14} style={{ color: accent }} className="mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-text-primary text-sm">{loc.name}</p>
                    {(loc.city || loc.address) && <p className="text-xs text-gray-500 mt-0.5">{[loc.city, loc.address].filter(Boolean).join(', ')}</p>}
                    <div className="flex gap-1.5 mt-1.5">
                      {loc.isOnline && <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">Онлайн</span>}
                      {loc.isMobile && <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">Выезд</span>}
                      {!loc.isOnline && !loc.isMobile && <span className="text-xs bg-bg-muted text-text-secondary px-2 py-0.5 rounded-full">Оффлайн</span>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {blocks.gallery && galleryPhotos.length > 0 && (
          <div className="bg-bg-surface rounded-2xl border border-border-subtle shadow-sm p-5">
            <h2 className="text-sm font-bold text-text-primary mb-3">Галерея работ <span className="text-gray-400 font-normal">({galleryPhotos.length})</span></h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px' }}>
              {galleryPhotos.map((photo: any) => (
                <div key={photo.id} style={{ position: 'relative', paddingBottom: '100%', borderRadius: '12px', overflow: 'hidden', background: 'var(--color-surface)' }}>
                  <img src={photo.url?.startsWith('http') ? photo.url : `https://api.svoyprofi.ru${photo.url}`} alt={photo.caption ?? ''}
                    style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
                  {photo.caption && <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: 'rgba(0,0,0,0.5)', padding: '4px 8px' }}><p style={{ color: '#fff', fontSize: '11px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{photo.caption}</p></div>}
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="bg-bg-surface rounded-2xl border border-border-subtle shadow-sm p-5">
          {hasServices && (
            <div className="flex gap-2 mb-4">
              <button onClick={() => setActiveTab('services')} className="px-4 py-1.5 rounded-lg text-sm font-medium transition-all" style={activeTab === 'services' ? { backgroundColor: accent, color: textOnAccent } : { backgroundColor: 'var(--color-surface)', color: 'var(--color-text-muted)' }}>Услуги ({orgServices.length})</button>
              <button onClick={() => setActiveTab('specialists')} className="px-4 py-1.5 rounded-lg text-sm font-medium transition-all" style={activeTab === 'specialists' ? { backgroundColor: accent, color: textOnAccent } : { backgroundColor: 'var(--color-surface)', color: 'var(--color-text-muted)' }}>Мастера ({acceptedMembers.length})</button>
            </div>
          )}
          {hasServices && activeTab === 'services' && (
            <div className="space-y-3">
              {orgServices.map((svc: any) => (
                <div key={svc.id} className="border border-border-subtle rounded-xl p-4 transition-all" onMouseEnter={e => (e.currentTarget.style.borderColor = `${accent}66`)} onMouseLeave={e => (e.currentTarget.style.borderColor = 'var(--color-surface)')}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-text-primary text-sm">{svc.title}</p>
                      {svc.description && <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">{svc.description}</p>}
                      <div className="flex flex-wrap gap-2 mt-1.5">
                        {svc.price && <span className="text-xs font-semibold" style={{ color: accent }}>{Number(svc.price).toLocaleString('ru-RU')} ₽</span>}
                        <span className="text-xs text-gray-400">{svc.durationMinutes} мин</span>
                      </div>
                      {svc.specialists.length > 0 && (
                        <div className="flex items-center gap-1.5 mt-2">
                          <div className="flex -space-x-1.5">
                            {svc.specialists.slice(0, 3).map((link: any) => {
                              const u = link.specialist.user; const name = getDisplayName(u);
                              return <div key={link.id} className="w-6 h-6 rounded-full border-2 border-white overflow-hidden flex items-center justify-center text-white text-xs font-bold flex-shrink-0" style={{ background: `linear-gradient(135deg, ${accent}, ${accent}aa)` }}>{u?.avatarUrl ? <img src={u.avatarUrl} alt={name} className="w-6 h-6 object-cover" /> : name.charAt(0)}</div>;
                            })}
                          </div>
                          <span className="text-xs text-gray-400">{svc.specialists.length} {svc.specialists.length === 1 ? 'мастер' : 'мастера'}</span>
                        </div>
                      )}
                    </div>
                    {svc.specialists.length > 0 && (
                      <button onClick={() => setBookingService(svc)} className="flex-shrink-0 flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold transition-all" style={{ backgroundColor: accent, color: textOnAccent }} onMouseEnter={e => (e.currentTarget.style.backgroundColor = accentHover)} onMouseLeave={e => (e.currentTarget.style.backgroundColor = accent)}>
                        <Calendar size={13} /> Записаться
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
          {(!hasServices || activeTab === 'specialists') && (
            <>
              <h2 className="text-sm font-bold text-text-primary mb-3 flex items-center gap-1.5"><Users size={14} style={{ color: accent }} /> Специалисты <span className="text-gray-400 font-normal">({acceptedMembers.length})</span></h2>
              {acceptedMembers.length === 0 ? <p className="text-gray-400 text-sm">Пока нет специалистов</p> : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {acceptedMembers.map((m: any) => {
                    const spec = m.specialist; const u = m.user; const name = getDisplayName(u);
                    const href = spec?.id ? `/specialists/${spec.id}` : null;
                    const services = spec?.services?.slice(0, 2).map((s: any) => s.title).join(' · ');
                    return (
                      <div key={m.id} className="flex flex-col gap-3 p-4 border border-border-subtle rounded-xl transition-all" onMouseEnter={e => (e.currentTarget.style.borderColor = `${accent}66`)} onMouseLeave={e => (e.currentTarget.style.borderColor = 'var(--color-surface)')}>
                        <div className="flex items-start gap-3">
                          <div className="w-11 h-11 rounded-full flex items-center justify-center text-white font-bold flex-shrink-0 overflow-hidden" style={{ background: `linear-gradient(135deg, ${accent}, ${accent}aa)` }}>
                            {u?.avatarUrl ? <img src={u.avatarUrl} alt={name} className="w-11 h-11 object-cover" /> : name.charAt(0).toUpperCase()}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-semibold text-text-primary text-sm">{name}</p>
                            {spec?.rating > 0 && <div className="flex items-center gap-1 mt-0.5"><Star size={11} className="text-yellow-400 fill-yellow-400" /><span className="text-xs font-medium text-text-secondary">{Number(spec.rating).toFixed(1)}</span>{spec.reviewsCount > 0 && <span className="text-xs text-gray-400">({spec.reviewsCount})</span>}</div>}
                            {services && <p className="text-xs text-gray-400 mt-0.5 truncate">{services}</p>}
                          </div>
                        </div>
                        {href && (
                          <div className="flex gap-2">
                            <button onClick={() => setBookingMember(m)} className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-sm font-semibold transition-all" style={{ backgroundColor: accent, color: textOnAccent }} onMouseEnter={e => (e.currentTarget.style.backgroundColor = accentHover)} onMouseLeave={e => (e.currentTarget.style.backgroundColor = accent)}>
                              <Calendar size={13} /> Записаться
                            </button>
                            {!org.hideSpecialistLinks && <Link href={href} className="flex items-center justify-center px-3 py-2 rounded-lg text-sm font-semibold transition-all border" style={{ borderColor: accent, color: accent }} title="Профиль специалиста"><ExternalLink size={14} /></Link>}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>

        {blocks.reviews && org.recentReviews?.length > 0 && (
          <div className="bg-bg-surface rounded-2xl border border-border-subtle shadow-sm p-5">
            <h2 className="text-sm font-bold text-text-primary mb-3 flex items-center gap-1.5"><Star size={14} style={{ color: accent }} /> Отзывы клиентов{org.totalReviews > 0 && <span className="text-gray-400 font-normal">({org.totalReviews})</span>}</h2>
            <div className="space-y-3">
              {org.recentReviews.map((review: any) => {
                const clientName = review.client?.displayName || `${review.client?.firstName ?? ''} ${review.client?.lastName ?? ''}`.trim() || 'Клиент';
                return (
                  <div key={review.id} className="border-b border-gray-50 last:border-0 pb-3 last:pb-0">
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0 overflow-hidden" style={{ background: `linear-gradient(135deg, ${accent}, ${accent}aa)` }}>
                          {review.client?.avatarUrl ? <img src={review.client.avatarUrl} alt={clientName} className="w-7 h-7 object-cover" /> : clientName.charAt(0).toUpperCase()}
                        </div>
                        <span className="text-sm font-semibold text-text-body">{clientName}</span>
                      </div>
                      <div className="flex items-center gap-0.5">{Array.from({ length: 5 }).map((_, i) => <Star key={i} size={11} className={i < review.rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-200 fill-gray-200'} />)}</div>
                    </div>
                    {review.comment && <p className="text-sm text-text-secondary ml-9 leading-relaxed">{review.comment}</p>}
                    <p className="text-xs text-gray-400 ml-9 mt-1">{new Date(review.createdAt).toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className="rounded-2xl p-5 text-center" style={{ backgroundColor: accent, color: textOnAccent }}>
          <h3 className="font-bold text-base mb-1">Готовы записаться?</h3>
          <p className="text-sm mb-4" style={{ color: textOnAccent, opacity: 0.8 }}>Выберите специалиста и удобное время прямо сейчас</p>
          <button onClick={() => { const el = document.querySelector('[data-specialists]'); el?.scrollIntoView({ behavior: 'smooth' }); }}
            className="inline-flex items-center gap-2 bg-bg-surface px-6 py-2.5 rounded-xl text-sm font-semibold transition-all hover:opacity-90" style={{ color: accent }}>
            <Calendar size={15} /> Выбрать специалиста
          </button>
        </div>
      </div>
    </div>
  );
}
